import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useLocation, useParams } from 'react-router-dom'
import { Box, Button, Typography } from '@mui/joy'
import { cleanCurrentTask, getProjectDetails, getTaskDetails, getTaskReserveUsers } from 'features/projectsSlice'
import routes from 'routes'
import Breadcrumbs from 'components/Breadcrumbs'
import MainLayout from 'components/templates/MainLayout'
import PageLoader from 'components/PageLoader'
import TaskInfoType1SubView from 'views/common/TaskInfoType1SubView'
import TaskInfoType2SubView from 'views/common/TaskInfoType2SubView'
import AssignedUserSubView from './common/AssignedUserSubView'
import useAssignUserManual from 'hooks/useAssignUserManual'
import ReserveUsersSubView from './common/ReserveUsersSubView'
import { PersonSearch } from '@mui/icons-material'
import AssignUserModal from 'components/AssignUserModal'

const getTaskName = (project, task) => {
  let name = ''
  if (!project || !task) return '?'
  if ([1, 3].includes(project.type)) name = `${task.lp || task.id} - ${task.localization}`
  else if (project.type === 2) name = `${task.lp || task.id}`
  return name
}

const AdminTaskDetailsView = () => {
  const dispatch = useDispatch()
  const { state } = useLocation()
  const { projectId, taskId } = useParams()

  const [innerLoading, setInnerLoading] = useState(false)

  const { currentTask, currentProject, loading } = useSelector((s) => s.projects)

  const { addCustomUser, setAddCustomUser, forcedMode, setForcedMode, handleAssignProjectTaskUser } =
    useAssignUserManual({
      projectId,
      taskId,
      innerLoading,
      setInnerLoading,
    })

  useEffect(() => {
    dispatch(getProjectDetails(projectId))
    dispatch(getTaskDetails({ projectId, taskId }))
    dispatch(getTaskReserveUsers({ projectId, taskId }))
    return () => dispatch(cleanCurrentTask())
  }, [])

  const getTaskInfo = () => {
    switch (currentProject.type) {
      case 1:
      case 3:
        return <TaskInfoType1SubView project={currentProject} task={currentTask} />
      case 2:
        return <TaskInfoType2SubView project={currentProject} task={currentTask} />
      default:
        break
    }
  }

  if (!projectId || !taskId) return <Navigate to={routes.adminProjects} />

  if (loading)
    return (
      <MainLayout>
        <PageLoader />
      </MainLayout>
    )

  if (!currentProject || !currentTask) return <MainLayout></MainLayout>

  return (
    <MainLayout>
      <AssignUserModal
        open={addCustomUser}
        setOpen={setAddCustomUser}
        onClose={() => {
          setAddCustomUser(false)
          setForcedMode(false)
        }}
        loading={innerLoading}
        forcedMode={forcedMode}
        setForcedMode={setForcedMode}
        onSubmit={handleAssignProjectTaskUser}
      />
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Breadcrumbs
          items={[
            { label: 'Projekty', to: routes.adminProjects },
            {
              label: currentProject?.name || state?.from || projectId,
              to: routes.adminProjectsDetails.replace(':projectId', projectId),
            },
            { label: getTaskName(currentProject, currentTask) },
          ]}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          mb: 1,
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'start', sm: 'center' },
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Typography level="h2" component="h1">
          Zadanie:&nbsp;
          <Typography color="primary">{currentTask?.localization}</Typography>
        </Typography>
        <Button color="primary" startDecorator={<PersonSearch />} onClick={() => setAddCustomUser(true)}>
          Przypisz użytkownika ręcznie
        </Button>
      </Box>

      {getTaskInfo()}

      <AssignedUserSubView innerLoading={innerLoading} setInnerLoading={setInnerLoading} />

      <ReserveUsersSubView
        innerLoading={innerLoading}
        setInnerLoading={setInnerLoading}
        handleAssignProjectTaskUser={handleAssignProjectTaskUser}
      />
    </MainLayout>
  )
}

export default AdminTaskDetailsView
